import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertCircle, CheckCircle, Clock, Send, FileText } from 'lucide-react';

interface ComplianceStats {
  total_agreements: number;
  pending_delivery: number;
  delivered: number;
  overdue: number;
  nearing_deadline: number;
  average_delivery_days: number;
}

interface Agreement {
  id: string;
  agreement_number: string;
  homeowner_name: string;
  homeowner_email: string;
  effective_date: string;
  delivery_deadline: string;
  delivery_status: string;
  days_remaining?: number;
  days_overdue?: number;
  delivery_attempts: number;
}

export default function ComplianceDashboard() {
  const [stats, setStats] = useState<ComplianceStats | null>(null);
  const [nearingDeadline, setNearingDeadline] = useState<Agreement[]>([]);
  const [overdue, setOverdue] = useState<Agreement[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
    
    // Refresh every 5 minutes
    const interval = setInterval(loadData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      await Promise.all([
        loadStats(),
        loadNearingDeadline(),
        loadOverdue()
      ]);
    } catch (error) {
      console.error('Error loading compliance data:', error);
    } finally {
      setLoading(false);
    }
  }

  async function loadStats() {
    const client = supabase;
    if (!client) {
      console.warn('Supabase client not configured');
      return;
    }

    const { data, error } = await client
      .from('warranty_agreements')
      .select('delivery_status, contract_generated_at, effective_date, delivery_deadline');

    if (error) {
      console.error('Error loading warranty stats:', error);
      return;
    }

    if (data) {
      const now = new Date();
      const delivered = data.filter(a => a.delivery_status === 'delivered');
      const avgDays = delivered.length > 0
        ? delivered.reduce((sum, a) => {
            const effectiveDate = new Date(a.effective_date);
            const deliveredDate = new Date(a.contract_generated_at || now);
            const daysDiff = Math.floor((deliveredDate.getTime() - effectiveDate.getTime()) / (1000 * 60 * 60 * 24));
            return sum + daysDiff;
          }, 0) / delivered.length
        : 0;

      setStats({
        total_agreements: data.length,
        pending_delivery: data.filter(a => a.delivery_status === 'pending').length,
        delivered: delivered.length,
        overdue: data.filter(a => {
          const deadline = new Date(a.delivery_deadline);
          return a.delivery_status !== 'delivered' && deadline < now;
        }).length,
        nearing_deadline: data.filter(a => {
          const deadline = new Date(a.delivery_deadline);
          const sevenDaysOut = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
          return a.delivery_status !== 'delivered' && deadline <= sevenDaysOut && deadline > now;
        }).length,
        average_delivery_days: Math.round(avgDays)
      });
    }
  }

  async function loadNearingDeadline() {
    const client = supabase;
    if (!client) {
      console.warn('Supabase client not configured');
      return;
    }

    const { data, error } = await client
      .rpc('get_contracts_nearing_deadline', { days_before: 7 });

    if (error) {
      console.error('Error loading nearing deadline agreements:', error);
      return;
    }

    if (data) {
      setNearingDeadline(data.map((item: any) => ({
        id: item.agreement_id,
        agreement_number: item.agreement_number,
        homeowner_name: item.homeowner_name,
        homeowner_email: item.homeowner_email,
        effective_date: item.effective_date,
        delivery_deadline: item.delivery_deadline,
        delivery_status: item.delivery_status,
        days_remaining: item.days_remaining,
        delivery_attempts: 0
      })));
    }
  }

  async function loadOverdue() {
    const client = supabase;
    if (!client) {
      console.warn('Supabase client not configured');
      return;
    }

    const { data, error } = await client
      .rpc('get_overdue_contracts');

    if (error) {
      console.error('Error loading overdue agreements:', error);
      return;
    }

    if (data) {
      setOverdue(data.map((item: any) => ({
        id: item.agreement_id,
        agreement_number: item.agreement_number,
        homeowner_name: item.homeowner_name,
        homeowner_email: item.homeowner_email,
        effective_date: item.effective_date,
        delivery_deadline: item.delivery_deadline,
        delivery_status: 'overdue',
        days_overdue: item.days_overdue,
        delivery_attempts: item.delivery_attempts
      })));
    }
  }

  async function resendContract(agreementId: string) {
    try {
      const client = supabase;
      if (!client) {
        throw new Error('Supabase client not configured');
      }
      // Queue for immediate delivery
      const { error } = await client.rpc('queue_contract_delivery', {
        p_agreement_id: agreementId,
        p_priority: 'urgent'
      });

      if (error) {
        throw error;
      }

      alert('✅ Contract queued for immediate delivery');
      loadData();
    } catch (error: any) {
      alert(`❌ Error: ${error.message}`);
    }
  }

  async function runWorkerNow() {
    try {
      const client = supabase;
      if (!client) {
        throw new Error('Supabase client not configured');
      }

      const { data, error } = await client.functions.invoke('contract-delivery-worker');

      if (error) throw error;

      alert(`✅ Worker ran: ${data?.results?.successful || 0} successful, ${data?.results?.failed || 0} failed`);
      loadData();
    } catch (error: any) {
      alert(`❌ Error: ${error.message}`);
    }
  }

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Contract Delivery Compliance</h1>
          <p className="text-gray-600 mt-2">
            Florida law requires contract delivery within 45 days of effective date
          </p>
        </div>
        <Button onClick={runWorkerNow}>
          <Send className="mr-2 h-4 w-4" />
          Run Delivery Worker Now
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Agreements</p>
              <p className="text-2xl font-bold">{stats?.total_agreements || 0}</p>
            </div>
            <FileText className="h-8 w-8 text-gray-400" />
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending Delivery</p>
              <p className="text-2xl font-bold text-yellow-600">{stats?.pending_delivery || 0}</p>
            </div>
            <Clock className="h-8 w-8 text-yellow-600" />
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Delivered</p>
              <p className="text-2xl font-bold text-green-600">{stats?.delivered || 0}</p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
        </Card>

        <Card className="p-4 bg-red-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-700 font-medium">Overdue</p>
              <p className="text-2xl font-bold text-red-600">{stats?.overdue || 0}</p>
            </div>
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
        </Card>

        <Card className="p-4 bg-yellow-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-yellow-700 font-medium">Nearing Deadline</p>
              <p className="text-2xl font-bold text-yellow-600">{stats?.nearing_deadline || 0}</p>
            </div>
            <Clock className="h-8 w-8 text-yellow-600" />
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Avg Delivery Time</p>
              <p className="text-2xl font-bold">{stats?.average_delivery_days || 0} days</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Overdue Contracts - Critical */}
      {overdue.length > 0 && (
        <Card className="p-6 border-red-200 bg-red-50">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <h2 className="text-xl font-semibold text-red-900">🚨 Overdue Contracts (Non-Compliant)</h2>
            </div>
            <span className="text-sm text-red-700">{overdue.length} agreements</span>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-red-200">
                  <th className="text-left p-2">Agreement #</th>
                  <th className="text-left p-2">Homeowner</th>
                  <th className="text-left p-2">Effective Date</th>
                  <th className="text-left p-2">Deadline</th>
                  <th className="text-left p-2">Days Overdue</th>
                  <th className="text-left p-2">Attempts</th>
                  <th className="text-left p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {overdue.map((agreement) => (
                  <tr key={agreement.id} className="border-b border-red-100">
                    <td className="p-2 font-mono text-sm">{agreement.agreement_number}</td>
                    <td className="p-2">{agreement.homeowner_name}</td>
                    <td className="p-2">{new Date(agreement.effective_date).toLocaleDateString()}</td>
                    <td className="p-2">{new Date(agreement.delivery_deadline).toLocaleDateString()}</td>
                    <td className="p-2">
                      <span className="px-2 py-1 bg-red-600 text-white rounded text-xs font-bold">
                        {agreement.days_overdue} days
                      </span>
                    </td>
                    <td className="p-2">{agreement.delivery_attempts}</td>
                    <td className="p-2">
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={() => resendContract(agreement.id)}
                      >
                        Resend Now
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Nearing Deadline - Warning */}
      {nearingDeadline.length > 0 && (
        <Card className="p-6 border-yellow-200 bg-yellow-50">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-yellow-600" />
              <h2 className="text-xl font-semibold text-yellow-900">⚠️ Nearing Deadline (Next 7 Days)</h2>
            </div>
            <span className="text-sm text-yellow-700">{nearingDeadline.length} agreements</span>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-yellow-200">
                  <th className="text-left p-2">Agreement #</th>
                  <th className="text-left p-2">Homeowner</th>
                  <th className="text-left p-2">Email</th>
                  <th className="text-left p-2">Deadline</th>
                  <th className="text-left p-2">Days Remaining</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {nearingDeadline.map((agreement) => (
                  <tr key={agreement.id} className="border-b border-yellow-100">
                    <td className="p-2 font-mono text-sm">{agreement.agreement_number}</td>
                    <td className="p-2">{agreement.homeowner_name}</td>
                    <td className="p-2 text-sm">{agreement.homeowner_email}</td>
                    <td className="p-2">{new Date(agreement.delivery_deadline).toLocaleDateString()}</td>
                    <td className="p-2">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${
                        (agreement.days_remaining || 0) <= 3 
                          ? 'bg-red-100 text-red-700' 
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {agreement.days_remaining} days
                      </span>
                    </td>
                    <td className="p-2">
                      <span className="px-2 py-1 bg-gray-100 rounded text-xs">
                        {agreement.delivery_status}
                      </span>
                    </td>
                    <td className="p-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => resendContract(agreement.id)}
                      >
                        Send Now
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* No Issues - Success State */}
      {overdue.length === 0 && nearingDeadline.length === 0 && !loading && (
        <Card className="p-12 text-center border-green-200 bg-green-50">
          <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-green-900 mb-2">
            ✅ 100% Compliant!
          </h2>
          <p className="text-green-700">
            All contracts are delivered within the 45-day Florida requirement
          </p>
        </Card>
      )}
    </div>
  );
}
